import { ControlEventTypes } from "Core/Events/EventTypes";
import { BaseDateControl } from "Controls/BaseDateControl";

/**
 * Control for editing dates.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Date with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class Date extends BaseDateControl {
    private _kendoElement: kendo.ui.DatePicker;

    /**
     * @hidden
     */
    protected dmsControlClass = "dms-control-date";

    /**
     * @hidden
     */
    protected bindingProperty = "customDate";

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.0.0
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
        this.jQueryElement.on("paste", (e) => this.onPaste(e));
        let options: kendo.ui.DatePickerOptions = {
            format: this.environment.DateFormat,
            culture: this.environment.Culture,
            change: (e) => this.trigger(ControlEventTypes.Change),
            dateInput: true,
            max: new globalThis.Date(2999, 31, 12),
            min: new globalThis.Date(1000, 1, 1),
        };
        this._baseKendoElement = this._kendoElement = new kendo.ui.DatePicker(this.jQueryElement[0], options);

        (<any>this._kendoElement)._dateInput.setOptions({
            messages: {
                month: "mm",
                day: "dd",
                year: "yyyy",
            },
        });
        
        this._kendoElement.wrapper.on("focus", ".k-input", () => {
            this.changeFocusCount = 0;
            setTimeout(() => this._kendoElement.wrapper.find(".k-input").select());
        });
        this._kendoElement.wrapper.on("keydown", ".k-input", (e) => {
            if ($.isNumeric(e.key)) {
                this.changeFocusCount++;
                if (this.changeFocusCount === 2) {
                    this.changeFocusCount = 0;
                    this.setNextFocus();
                }
            }
        });
    }

    /**
     * Set the minimum date for the datepicker.
     * @param date Minimum date.
     * @since 4.0.0
     */
    public setMin(date: globalThis.Date): void {
        this._kendoElement.min(date);
    }

    /**
     * Get the minimum date for the datepicker.
     * @since 4.0.0
     */
    public getMin(): globalThis.Date {
        return this._kendoElement.min();
    }

    /**
     * Set the maximum date for the datepicker.
     * @param date Maximum date.
     * @since 4.0.0
     */
    public setMax(date: globalThis.Date): void {
        this._kendoElement.max(date);
    }

    /**
     * Get the maximum date for the datepicker.
     * @since 4.0.0
     */
    public getMax(): globalThis.Date {
        return this._kendoElement.max();
    }

    /**
     * Set the control value.
     * @param value Value as Date or string.
     * @since 4.0.0
     */
    public setValue(value: globalThis.Date | string): void {
        let oldValue = this.getValue();
        let newValue: globalThis.Date = this.parseValue(value);

        if (oldValue !== newValue) {
            this._kendoElement.value(newValue);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Get control value.
     * @since 4.0.0
     */
    public getValue(): globalThis.Date {
        return this._kendoElement.value();
    }

    /**
     * Get the kendo datepicker control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/datepicker|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.DatePicker {
        return this._kendoElement;
    }

    /**
     * Gets the display template for formatting in grid row.
     * @hidden
     */
    protected getDisplayTemplate(bindingField?: string): string {
        return `#= ${bindingField} == null ? "${
            this.controlDefinition.ReplaceNullValue || ""
        }"  : DMS.Util.formatDate(new Date(${bindingField})) # `;
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.element.prop("disabled");
    }

    /**
     * Get the parsed default value for this control.
     * @hidden
     */
    public getDefaultValue(): Object {
        let value = super.getDefaultValue();
        if (value && value instanceof globalThis.Date) {
            value = kendo.timezone.convert(value, "Etc/UTC", this.environment.ClientTimeZone);
        }
        return value;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement != null) {
            this.jQueryElement.off("focusout");
            this.jQueryElement.off("paste");
        }
    }
}
